<template>
  <v-card
    elevation="1"
    rounded="lg"
    class="portal-home-dashboard__section-card-elevated h-100 bg-surface"
    :class="embedded ? 'portal-ultimos-informes--embedded' : ''"
  >
    <!-- Escritorio: título -->
    <v-card-title
      v-if="!embedded"
      class="py-2 px-3 text-subtitle-2 font-weight-bold portal-home-dashboard__section-title-tonal d-flex align-center gap-2 flex-wrap"
    >
      <span class="d-inline-flex align-center flex-grow-1 min-w-0 gap-2">
        <span class="text-wrap">Últimos informes</span>
        <v-badge
          v-if="informesTablaBadge > 0"
          :content="String(informesTablaBadge)"
          color="primary"
          inline
          class="mb-1 ml-1"
        />
      </span>
    </v-card-title>
    <v-divider />

    <!-- Escritorio: tabla -->
    <v-data-table
      v-if="!embedded"
      class="portal-home-dashboard__informes-table"
      color="primary"
      :headers="headers"
      :items="informesHome.items"
      :loading="informesHome.loading"
      density="compact"
      hide-default-footer
      :items-per-page="-1"
      item-value="id"
      no-data-text="No hay informes disponibles"
    >
      <template #[`item.informe`]="{ item }">
        <span
          class="text-uppercase font-weight-medium portal-ultimos-informes__tabla-informe"
        >
          {{ item.informe }}
        </span>
      </template>
      <template #[`item.acciones`]="{ item }">
        <div class="portal-table-actions d-flex flex-wrap justify-end align-center py-1">
          <v-btn
            v-if="puedeVerEstudio(item)"
            variant="outlined"
            color="primary"
            density="compact"
            class="portal-table-action-btn text-none"
            :prepend-icon="openInNewIcon"
            @click.stop="onVerEstudio(item)"
          >
            Ver estudio
          </v-btn>
          <v-btn
            v-if="puedeVerInforme(item)"
            color="primary"
            variant="flat"
            density="compact"
            class="portal-table-action-btn text-none"
            :prepend-icon="fileDocIcon"
            @click.stop="onVerInformePdf(item)"
          >
            Ver informe
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- Móvil: lista en tarjetas -->
    <div v-else class="portal-ultimos-informes__cards-wrap pa-3">
      <v-progress-linear
        v-if="informesHome.loading"
        indeterminate
        color="primary"
        class="mb-3 rounded"
        height="3"
      />
      <template v-else-if="informesHome.items.length === 0">
        <div
          class="text-body-2 text-medium-emphasis text-center py-8"
        >
          No hay informes disponibles
        </div>
      </template>
      <div v-else class="d-flex flex-column gap-3">
        <v-card
          v-for="item in informesHome.items"
          :key="item.id"
          variant="outlined"
          rounded="lg"
          class="portal-ultimos-informes__item-card pa-4 bg-surface my-1"
        >
          <div
            class="portal-ultimos-informes__header-row mb-2"
          >
            <div class="portal-ultimos-informes__header-main">
              <v-avatar
                size="40"
                color="primary"
                variant="tonal"
                class="portal-ultimos-informes__header-avatar flex-shrink-0"
              >
                <v-icon :icon="fileDocIcon" size="24" />
              </v-avatar>
              <span
                class="text-subtitle-2 font-weight-bold text-uppercase text-wrap portal-ultimos-informes__nombre"
              >
                {{ item.informe }}
              </span>
            </div>
          </div>
          <div
            class="d-flex align-center gap-2 text-body-2 text-medium-emphasis mb-3 portal-ultimos-informes__fecha-fila"
          >
            <v-icon :icon="calendarIcon" size="18" class="flex-shrink-0 mr-1" />
            <span>{{ fechaMobileCorta(item) }}</span>
          </div>
          <div class="d-flex gap-2">
            <v-btn
              v-if="puedeVerEstudio(item)"
              variant="outlined"
              color="primary"
              class="flex-grow-1 text-none mr-1"
              :prepend-icon="openInNewIcon"
              @click="onVerEstudio(item)"
            >
              Ver estudio
            </v-btn>
            <v-btn
              v-if="puedeVerInforme(item)"
              color="primary"
              variant="flat"
              class="flex-grow-1 text-none"
              :prepend-icon="fileDocIcon"
              @click="onVerInformePdf(item)"
            >
              Ver informe
            </v-btn>
          </div>
        </v-card>
      </div>
    </div>
  </v-card>

  <v-overlay
    :model-value="pdfLoading"
    persistent
    scroll-strategy="none"
    attach="body"
    :z-index="4500"
    class="portal-ultimos-informes__pdf-fetch-overlay"
  >
    <v-progress-circular
      color="primary"
      indeterminate
      :size="80"
      :width="8"
    />
  </v-overlay>

  <v-dialog
    v-model="pdfDialogOpen"
    fullscreen
    transition="dialog-bottom-transition"
    scroll-strategy="none"
    content-class="portal-ultimos-informes__pdf-dialog-content"
    @update:model-value="onPdfDialogUpdate"
  >
    <v-card
      rounded="0"
      class="portal-ultimos-informes__pdf-dialog-card d-flex flex-column bg-surface"
    >
      <v-toolbar
        density="comfortable"
        class="flex-shrink-0 border-b portal-ultimos-informes__pdf-toolbar-tonal"
      >
        <v-toolbar-title class="text-body-1 font-weight-bold">
          {{ pdfTitulo }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn
          :icon="closeIcon"
          variant="text"
          aria-label="Cerrar"
          @click="pdfDialogOpen = false"
        />
      </v-toolbar>
      <div class="portal-ultimos-informes__pdf-iframe-wrap">
        <PortalPdfViewer
          v-if="pdfBlob"
          :key="pdfIframeKey"
          :blob="pdfBlob"
        />
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";

import PortalPdfViewer from "../../../components/shared/PortalPdfViewer.vue";
import icons from "../../../utils/enums/icons";
import {
  informeRowPuedeVerEstudio,
  informeRowPuedeVerInforme,
  TEXTO_SIN_FECHA_INFORME,
  formatInformeFechaMobile,
} from "../../../utils/helpers/informesHomeMappers";

export default {
  name: "PortalHomeUltimosInformesCard",
  components: { PortalPdfViewer },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      closeIcon: icons.CLOSE,
      openInNewIcon: icons.OPEN_IN_NEW,
      fileDocIcon: icons.FILE,
      calendarIcon: icons.CALENDAR,
      headers: [
        { title: "Informe", key: "informe", sortable: true, align: "start" }, 
        {
          title: "Fecha",
          key: "fechaDisplay",
          sortable: true,
          align: "start",
        },
        {
          title: "Acciones",
          key: "acciones",
          sortable: false,
          align: "end",
        },
      ],
      pdfDialogOpen: false,
      pdfIframeKey: 0,
      pdfLoading: false,
      pdfBlob: null,
      pdfTitulo: "Informe",
      pdfInfoId: null,
      /** Igual que geclisa-site (ConvertirEnInterado): ocultar scroll del documento, no solo bloquearlo. */
      pdfRootScrollLockApplied: false,
      pdfSavedHtmlOverflow: "",
      pdfSavedBodyOverflow: "",
    };
  },
  computed: {
    ...mapState("portalHome", ["informesHome"]),
    /** Cantidad de filas cargadas en la tabla / tarjetas (no el resumen de tiles del home). */
    informesTablaBadge() {
      const items = this.informesHome?.items;
      return Array.isArray(items) ? items.length : 0;
    },
  },
  mounted() {
    this.$nextTick(() => {
      void this.fetchInformesUltimos();
    });
  },
  watch: {
    pdfDialogOpen() {
      this.syncPdfRootDocumentScroll();
    },
    pdfLoading() {
      this.syncPdfRootDocumentScroll();
    },
  },
  methods: {
    ...mapActions({
      fetchInformesUltimos: 'portalHome/fetchInformesUltimos',
      fetchInformeEstudioUrlImagen: 'portalHome/fetchInformeEstudioUrlImagen',
      fetchInformePdfBlob: 'portalHome/fetchInformePdfBlob',
      fetchInformeLaboratorioPdfLisBlob: 'portalHome/fetchInformeLaboratorioPdfLisBlob',
    }), 
    puedeVerEstudio(row) {
      return informeRowPuedeVerEstudio(row);
    },
    puedeVerInforme(row) {
      return informeRowPuedeVerInforme(row);
    },
    fechaMobileCorta(item) {
      const raw = item?.fechaSort;
      if (raw != null && raw !== "") {
        return formatInformeFechaMobile(raw);
      }
      const disp = item?.fechaDisplay;
      if (disp && disp !== TEXTO_SIN_FECHA_INFORME) return disp;
      return TEXTO_SIN_FECHA_INFORME;
    },
    async onVerEstudio(row) {
      const mpId = row.mpId;
      if (mpId == null || mpId <= 0) return;
      try {
        const url = await this.fetchInformeEstudioUrlImagen(mpId);
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
        }
      } catch {
        /* alerta vía interceptor axios */
      }
    },
    async onVerInformePdf(row) {
      const infoId = row?.infoId;
      if (infoId == null || infoId <= 0) return;
      this.revokePdfUrl();
      this.pdfLoading = true;
      try {
        const blob = row?.esLaboratorio
          ? await this.fetchInformeLaboratorioPdfLisBlob(row.infoLabId || infoId)
          : await this.fetchInformePdfBlob(infoId);
        this.pdfBlob = blob;
        this.pdfIframeKey += 1;
        this.pdfInfoId = infoId;
        this.pdfTitulo = `Informe N°${infoId}`;
        this.pdfDialogOpen = true;
        await this.$nextTick();
      } catch {
        this.revokePdfUrl();
      } finally {
        this.pdfLoading = false;
      }
    },
    revokePdfUrl() {
      this.pdfBlob = null;
      this.pdfInfoId = null;
    },
    onPdfDialogUpdate(open) {
      if (!open) this.revokePdfUrl();
    },
    /** Oculta el scrollbar de `html`/`body` (p. ej. geclisa-site ConvertirEnInterado). */
    syncPdfRootDocumentScroll() {
      const need = this.pdfDialogOpen || this.pdfLoading;
      if (need) this.applyPdfRootDocumentScroll();
      else this.releasePdfRootDocumentScroll();
    },
    applyPdfRootDocumentScroll() {
      if (this.pdfRootScrollLockApplied) return;
      this.pdfRootScrollLockApplied = true;
      const html = document.documentElement;
      const body = document.body;
      this.pdfSavedHtmlOverflow = html.style.overflow;
      this.pdfSavedBodyOverflow = body.style.overflow;
      html.style.setProperty("overflow", "hidden");
      body.style.setProperty("overflow", "hidden");
      window.scrollTo(0, 0);
    },
    releasePdfRootDocumentScroll() {
      if (!this.pdfRootScrollLockApplied) return;
      this.pdfRootScrollLockApplied = false;
      const html = document.documentElement;
      const body = document.body;
      if (this.pdfSavedHtmlOverflow) {
        html.style.overflow = this.pdfSavedHtmlOverflow;
      } else {
        html.style.removeProperty("overflow");
      }
      if (this.pdfSavedBodyOverflow) {
        body.style.overflow = this.pdfSavedBodyOverflow;
      } else {
        body.style.removeProperty("overflow");
      }
    },
  },
  beforeUnmount() {
    this.releasePdfRootDocumentScroll();
  },
};
</script>

<style scoped>
.portal-home-dashboard__section-card-elevated {
  border: 1px solid #eceff1;
}
.portal-dark-mode .portal-home-dashboard__section-card-elevated {
  border-color: rgba(255, 255, 255, 0.1);
}
.portal-home-dashboard__section-title-tonal {
  color: rgb(var(--v-theme-primary)) !important;
  line-height: 1.35;
  background-color: rgba(var(--v-theme-primary), 0.12) !important;
  font-size: 20px !important;
  font-weight: bold !important;
}
.portal-ultimos-informes__pdf-fetch-overlay :deep(.v-overlay__content) {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
}
.portal-ultimos-informes--embedded {
  box-shadow: none !important;
  border: none !important;
}
.portal-ultimos-informes__pdf-toolbar-tonal {
  color: rgb(var(--v-theme-primary)) !important;
  background-color: rgba(var(--v-theme-primary), 0.12) !important;
}
.portal-ultimos-informes__pdf-dialog-card {
  height: 100%;
  max-height: 100dvh;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}
.portal-ultimos-informes__pdf-iframe-wrap {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.portal-home-dashboard__informes-table :deep(thead th),
.portal-home-dashboard__informes-table :deep(thead .v-data-table__th),
.portal-home-dashboard__informes-table :deep(.v-data-table-header__content) {
  font-weight: 700 !important;
  color: #424242 !important;
}
.portal-home-dashboard__informes-table :deep(.v-data-table__td-sort-icon),
.portal-home-dashboard__informes-table :deep(.v-data-table__td-sort-icon-active) {
  color: rgb(var(--v-theme-primary)) !important;
  opacity: 1;
}
.portal-home-dashboard__informes-table :deep(.v-data-table-progress .v-progress-linear),
.portal-home-dashboard__informes-table :deep(.v-data-table-progress .v-progress-circular) {
  color: rgb(var(--v-theme-primary)) !important;
}
.portal-home-dashboard__informes-table :deep(.v-data-table__td) {
  white-space: normal;
  word-break: break-word;
  vertical-align: center;
}
.portal-ultimos-informes__tabla-informe {
  display: inline-block;
  max-width: 100%;
}
.portal-ultimos-informes__header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}
.portal-ultimos-informes__header-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1 1 auto;
}
.portal-ultimos-informes__header-avatar {
  align-self: center;
}
.portal-ultimos-informes__nombre {
  flex: 1 1 auto;
  min-width: 0;
  margin-left: 0;
  padding-top: 1px;
  overflow-wrap: anywhere;
  line-height: 1.35;
}
.portal-ultimos-informes__fecha-fila {
  padding-left: 10px;
  margin-left: 4px;
}
.portal-ultimos-informes__item-card {
  border-color: rgba(0, 0, 0, 0.08) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06) !important;
}
</style>

<style>
/* content-class del v-dialog se aplica en el overlay (teleport), fuera del scope del SFC */
.portal-ultimos-informes__pdf-dialog-content {
  padding: 0 !important;
  margin: 0 !important;
  overflow: hidden !important;
  height: 100% !important;
  max-height: 100dvh !important;
  width: 100% !important;
  max-width: 100% !important;
}
</style>
